import React from 'react'

import Image from '../../../../../components/pdf/Image'
import Separator from '../../../../../components/pdf/Separator'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import { Bullets, Paragraph, SectionHeading, SubHeading } from './_shared/Prose'
import { RedDataCell, RedHeadCell, RedRow, RedTable } from './_shared/BradescoTable'
import { TABLE_ACCENT_DARK_RED } from '../_helpers/brand'

/**
 * Seção 10 - Classificação dos Riscos: Ergonômico/Psicossociais e Biomecânicos. Texto
 * literal do modelo aprovado por Julia Buzzo ("Modelo PGR 2026 - Banco Bradesco_Varejo"),
 * item 10 (págs. 33-38), extraído com `pdftotext -raw` (o `-layout` intercala a marca
 * d'água diagonal "EXEMPLO CONFIDENCIAL - NÃO REPRODUZIR" linha a linha com o texto real).
 *
 * SUBSTITUI POR COMPLETO o conteúdo anterior deste arquivo/slot: o arquivo se chamava
 * `10.GraduacaoProbabilidadeSection.js` e renderizava, sob o título "10. GRADAÇÃO DE
 * PROBABILIDADE", uma tabela que na realidade é a subseção 9.2 do modelo (já reproduzida
 * corretamente em `09.ClassificacaoRiscosFisicoQuimicoBiologicoSection.js`, junto com a
 * gradação de Severidade, a Matriz de Risco e a Avaliação e Prioridade de Controle — 9.2 a
 * 9.4). Ou seja, a Seção 10 do documento real NUNCA existiu no PDF gerado: o número "10"
 * era usado para duplicar conteúdo da Seção 9, e a Seção 10 verdadeira (esta aqui) não
 * estava implementada. Julia reportou isso ("o ponto 10 11 e 12 estão errado... precisa
 * seguir igual ao modelo") e confirmou que pode ser apagado e refeito.
 *
 * Ver `PdfDocument.js` para o mapeamento completo antes/depois das Seções 10-12 e
 * `11.PlanoDeAcaoDefinicaoAplicabilidadeSection.js` / `12.MonitoramentoExposicaoSection.js`
 * para o que preencheu os slots de arquivo "11" e "12" (conteúdo que antes vivia,
 * corretamente rotulado com `numero={11}`/`numero={12}`, dentro do arquivo "13").
 *
 * Fora do escopo do cálculo dinâmico: a matriz 4x5 de probabilidade x severidade psicossocial
 * (10.3) e a tabela "Gradação da Severidade" (10.2) são, aqui, texto/tabela estáticos —
 * Julia confirmou anteriormente que o cálculo do Inventário de Riscos (Seção 18) usa apenas
 * a matriz 5x5 da Seção 9 (`_helpers/riskEngine.js`, MATRIZ_RISCO/PROBABILIDADE_REFERENCIA/
 * SEVERIDADE_REFERENCIA); não há, em nenhum helper, um motor de cálculo para o método
 * ERGOCHECK/PREMAPA — não foi criado nenhum aqui, o conteúdo é só o texto de referência
 * institucional do modelo, como as demais seções 2-9/13-20.
 *
 * A paleta de cores da tabela "Gradação da Severidade"/matriz 4x5 (Inelegível/Aceitável/
 * Moderado/Alto/Crítico) foi confirmada por amostragem de pixel do PDF-modelo renderizado:
 * verde #8ED873, amarelo #FFFF00, laranja #FFC000, vermelho #FF0000 (mesmos 4 tons já usados
 * em `RISCO_CELL_COLORS`, um nível abaixo) e um tom novo, roxo #77206E, para "Crítico" — sem
 * equivalente em `RISCO_CELL_COLORS` porque a escala de Seção 9 (Trivial-Crítico) não tem
 * esse quinto nível/cor.
 *
 * Print da tela do software "Ergocheck-Premapa" (10.2): extraído limpo (sem marca d'água,
 * que é uma camada de anotação separada) diretamente da imagem embutida no PDF-modelo via
 * `pdfimages`, mesma técnica já usada para `treinamento-assedio-integrarh.png` (Seção 4) —
 * salvo em `public/pdfs/bradesco/ergocheck-premapa-screen.png`.
 */

const RISCO_PSICOSSOCIAL_CELL_COLORS = {
  Inelegível: '#8ED873',
  Aceitável: '#FFFF00',
  Moderado: '#FFC000',
  Alto: '#FF0000',
  Crítico: '#77206E'
}

const isDarkFill = classificacao => classificacao === 'Alto' || classificacao === 'Crítico'

// Tabela "Os eventos sentinelas mais significativos e seu peso para o cálculo do índice
// de gravidade" (10.2, pág. 34).
const EVENTOS_SENTINELA = [
  {
    indicador: 'Doenças ocupacionais relatadas atribuíveis ao estresse relacionado ao trabalho.',
    peso: '3',
    motivacao: 'A presença de uma condição clínica atribuível ao estresse relacionado ao trabalho'
  },
  {
    indicador: 'Ausências por motivo de doença',
    peso: '3',
    motivacao:
      'É o indicador que a literatura científica aponta com tendo associações mais consolidadas com uma condição de estresse relacionado ao trabalho'
  },
  {
    indicador: 'Rotação de pessoal (faturamento negativo)',
    peso: '1',
    motivacao:
      'O menor peso se deve ao fato de que esse indicador depende em parte do mercado de trabalho e na literatura científica não há associação específica com condições de estresse.'
  },
  {
    indicador: 'Lesões',
    peso: '1',
    motivacao:
      'O estresse pode ter impacto no número de lesões, embora estas estejam relacionadas principalmente às condições físicas de trabalho e à cultura de segurança.'
  },
  {
    indicador: 'Visitas médicas a pedido do trabalhador',
    peso: '3',
    motivacao:
      'As consultas médicas solicitadas pelo trabalhador podem não depender de uma condição estressante, mas ainda assim são um indicador da saúde do trabalhador ou de dificuldades específicas em atender às demandas do trabalho.'
  },
  {
    indicador: 'Sanções disciplinares',
    peso: '0,5',
    motivacao:
      'É um indicador pouco estudado na literatura e, em geral, é altamente influenciado pela cultura corporativo; porém, este último não é indiferente aos aspectos psicossociais.'
  }
]

// Tabela "Gradação da Severidade" (10.2, pág. 36).
const GRADACAO_SEVERIDADE = [
  {
    classificacao: 'Inelegível',
    descricao: 'A avaliação não é prioritária, mas deve ser efetuada, pode ser considerado como uma condição exigível.',
    faixa: '0-20%'
  },
  {
    classificacao: 'Aceitável',
    descricao: 'A avaliação não tem prioridade, mas podem existir elementos de risco, é considerada uma maior exigência.',
    faixa: '21-40%'
  },
  {
    classificacao: 'Moderado',
    descricao: 'A avaliação é prioritária e podem existir situações que poderão requerer recursos profissionais especializados.',
    faixa: '41-60%'
  },
  {
    classificacao: 'Alto',
    descricao:
      'A avaliação é PRIORITÁRIA e as situações críticas necessitam de avaliação com elevados recursos profissionais, com a QUANTIFICAÇÃO de todos os fatores de stress e estabelecer mecanismos específicos de prevenção, através de metodologia cientificamente reconhecida.',
    faixa: '61-95%'
  },
  {
    classificacao: 'Crítico',
    descricao:
      'A avaliação é PRIORITÁRIA e URGENTE e as situações críticas necessitam de avaliação com elevados recursos profissionais, com QUANTIFICAÇÃO de todos os fatores de stress e estabelecer mecanismos específicos de prevenção, através de metodologia cientificamente reconhecida.',
    faixa: '95% OU UM QUADRO CRÍTICO'
  }
]

// Legenda "Probabilidade (Eventos Sentinelas)" — rótulos por linha, sem coluna de faixa
// (10.3, pág. 37, tabela da esquerda). Note que aqui não há o nível "Aceitável" — a
// escala de probabilidade usa "Baixo" no lugar (só a escala de Severidade usa "Aceitável").
const LEGENDA_PROBABILIDADE = ['Inelegível', 'Baixo', 'Moderado', 'Alto', 'Crítico']

// Legenda "Severidade (Ergocheck)" com faixa de score — 10.3, pág. 37, tabela da direita.
const LEGENDA_SEVERIDADE = [
  { classificacao: 'Inelegível', score: '0% a 23%' },
  { classificacao: 'Aceitável', score: '24% a 47,5%' },
  { classificacao: 'Baixo', score: '47,6% a 73,5%' },
  { classificacao: 'Alto', score: '73,6% a 95%' },
  { classificacao: 'Crítico', score: '>95%' }
]

// Matriz de Risco Final 4x5 (probabilidade x severidade psicossocial) — 10.3, pág. 37.
// Estática (texto/tabela de referência do modelo): não há motor de cálculo ERGOCHECK em
// `_helpers/riskEngine.js` — Julia confirmou que o cálculo do Inventário (Seção 18) usa
// apenas a matriz 5x5 da Seção 9. Colunas = Severidade (Inelegível¹ a Crítico⁵), linhas =
// Probabilidade (Alto⁴ a Inelegível¹, ordem decrescente como no modelo).
const MATRIZ_4X5_COLUNAS = [
  { label: 'Inelegível', numero: 1 },
  { label: 'Aceitável', numero: 2 },
  { label: 'Moderado', numero: 3 },
  { label: 'Alto', numero: 4 },
  { label: 'Crítico', numero: 5 }
]
const MATRIZ_4X5_LINHAS = [
  { label: 'Alto', numero: 4, celulas: ['Aceitável', 'Moderado', 'Alto', 'Alto', 'Crítico'] },
  { label: 'Moderado', numero: 3, celulas: ['Aceitável', 'Aceitável', 'Moderado', 'Alto', 'Crítico'] },
  { label: 'Baixo', numero: 2, celulas: ['Inelegível', 'Aceitável', 'Moderado', 'Moderado', 'Alto'] },
  { label: 'Inelegível', numero: 1, celulas: ['Inelegível', 'Inelegível', 'Aceitável', 'Moderado', 'Moderado'] }
]

// Tabela "Avaliação e Prioridade de Controle" (10.4, pág. 38).
const AVALIACAO_PRIORIDADE_CONTROLE = [
  {
    exposicao: 'Inelegível',
    resultado: 'Indica que a exposição está dentro de limites aceitáveis, sendo recomendado manter as condições de trabalho'
  },
  {
    exposicao: 'Aceitável',
    resultado: 'Sugere que a condição pode ser mantida, mas requer apoio e monitoramento constante dos elementos críticos.'
  },
  {
    exposicao: 'Moderado',
    resultado: 'Demonstra necessidade de melhoria, devendo ser implementadas ações corretivas ou compensatórias para reduzir os riscos.'
  },
  {
    exposicao: 'Alto',
    resultado:
      'Indica um cenário de risco elevado, sendo necessário recuperar a condição por meio da implementação de medidas corretivas ou compensatórias o mais rapidamente possível.'
  },
  {
    exposicao: 'Crítico',
    resultado: 'Representa situação grave de exposição, exigindo ajustes urgentes com introdução imediata de medidas corretivas ou compensatórias.'
  }
]

const ClassificacaoRiscosErgonomicoPsicossocialSection = () => (
  <View id="10">
    <Separator weight={15} />
    <SectionHeading numero={10}>CLASSIFICAÇÃO DOS RISCOS: ERGONÔMICO/PSICOSSOCIAIS E BIOMECÂNICOS</SectionHeading>

    <SubHeading id="10.1">10.1. Metodologia de Avaliação</SubHeading>
    <Paragraph>
      Com o objetivo de assegurar a identificação e avaliação eficaz dos riscos psicossociais no ambiente de
      trabalho, o Banco Bradesco S.A. adotou o método ERGOCHECK como ferramenta oficial para aplicação nas agências
      bancárias em consonância com o conceito e tabelas da ISO 45003, que utiliza os dados intitulados como "eventos
      sentinela" para definição da probabilidade e os critérios do seu questionário próprio para definir severidade.
    </Paragraph>
    <Paragraph>
      A avaliação de riscos tem como finalidade classificar e hierarquizar os perigos identificados, a partir da
      análise combinada de dois critérios fundamentais: probabilidade de ocorrência e severidade (potencial de danos
      à saúde).
    </Paragraph>
    <Paragraph>
      Utiliza-se uma matriz de risco para avaliação que permite estabelecer o nível de risco por meio dessa
      combinação, permitindo o direcionamento das ações de controle. Para este processo, adota-se uma abordagem
      baseada em metodologias reconhecidas internacionalmente, como as da American Industrial Hygiene Association
      (AIHA), Normas ISO e Bases técnicas da EPM – Internacional Ergonomics School, possibilitando decisões
      fundamentadas e priorização das medidas de intervenção.
    </Paragraph>
    <Paragraph>
      O mapeamento científico e prático da metodologia se divide em 4 partes: Conteúdo do Trabalho, Contexto de
      Trabalho, Evento Sentinela e Resultado do Mapa de Risco para o Estresse Relacionado ao Trabalho.
    </Paragraph>
    <Paragraph>
      Para cada análise biomecânica ocupacional há uma matriz de risco própria baseada em métodos validados
      cientificamente, que correlacionam exposição, demanda biomecânica e probabilidade de lesão.
    </Paragraph>
    <Paragraph>Exemplos consolidados incluem:</Paragraph>
    <Bullets
      items={[
        'Movimento Repetitivos: OCRA Index (Occupational Repetitive Actions) desenvolvido pela EPM Research Unit;',
        'Levantamento Manual de Cargas: Lifting Equation que estabelece limites biomecânicos;',
        'Análise de Esforços e Empurrar/Puxar: Snook & Ciriello Tables;'
      ]}
    />

    <SubHeading id="10.2">10.2. Gradação de Probabilidade e Severidade</SubHeading>
    <Paragraph>
      A Probabilidade foi extraída dos eventos "sentinela" dos últimos 3 anos, por meio da análise estatísticas dos
      dados fornecidos pelo Banco Bradesco S/A. Essa metodologia permite uma análise estruturada das condições
      organizacionais, relacionais e individuais que podem impactar a saúde mental e o bem-estar dos funcionários.
    </Paragraph>
    <Paragraph>
      Esses dados atuam como sinais de alerta precoce, permitindo a identificação de situações de risco e
      possibilitando a atuação em prevenção primária. Ou seja, são utilizados como base para a probabilidade de
      ocorrência do risco psicossocial. Os dados dos eventos sentinela incluem:
    </Paragraph>
    <Bullets
      items={[
        'Absenteísmo;',
        'Demissão voluntária e eventos disciplinares;',
        'Afastamentos por doenças relacionadas ao trabalho;',
        'Acidentes de trabalho (B-91).'
      ]}
    />
    <Paragraph>
      Consideramos os eventos sentinelas como "sinais de dano", ainda que iniciais, e não indicadores de risco
      potencial. Leva-se em consideração para o cálculo do índice de gravidade sintético do Grupo Homogêneo de
      Exposição.
    </Paragraph>
    <Paragraph>
      Os eventos sentinelas mais significativos e seu peso para o cálculo do índice de gravidade são ilustrados na
      tabela abaixo:
    </Paragraph>

    <View style={{ overflowX: 'auto' }}>
      <RedTable width="100%">
        <RedRow>
          <RedHeadCell width="34%" align="left">
            Indicador
          </RedHeadCell>
          <RedHeadCell width="10%">Peso</RedHeadCell>
          <RedHeadCell align="left">Motivação do Peso</RedHeadCell>
        </RedRow>
        {EVENTOS_SENTINELA.map((item, index) => (
          <RedRow key={item.indicador} striped={index % 2 === 1}>
            <RedDataCell align="left">{item.indicador}</RedDataCell>
            <RedDataCell align="center" bold>
              {item.peso}
            </RedDataCell>
            <RedDataCell align="left">{item.motivacao}</RedDataCell>
          </RedRow>
        ))}
      </RedTable>
    </View>

    <Separator weight={10} />

    <Paragraph>As gradações de probabilidade são 4 (quatro): Inelegível; Baixo; Moderado; Alto e Crítico.</Paragraph>
    <Paragraph>
      A Severidade foi obtida por meio da coleta organizacional dos dados, isto é, o estudo foi realizado in loco,
      somada a entrevista durante o acompanhamento de uma jornada de trabalho. Com intuito de coletar percepções do
      trabalhador nos seguintes temas:
    </Paragraph>
    <Bullets
      items={[
        'Carga de trabalho e ritmo de trabalho;',
        'Equilíbrio entre vida pessoal e profissional, insegurança com o trabalho e abuso;',
        'Ambiente de trabalho e equipamentos;',
        'Risco de roubo ou assédio;',
        'Horário de trabalho;',
        'Contato direto com usuários e complexidade no trabalho;',
        'Relações interpessoais e isolamento e Contato com sofrimento humano.'
      ]}
    />
    <Paragraph>
      Sendo assim, a avaliação e análise dos Fatores Ergonômicos Psicossociais do PREMAPA (Prevenção e Mapeamento
      dos Agravos Psicossociais no Ambiente de Trabalho), se dá através do percentual de presença do fator de risco
      em relação aos temas avaliados em questionário sobre as percepções do trabalho. Após o levantamento dos
      resultados, estes dados são analisados através do software Ergocheck-Premapa pautado na ISO 45003.
    </Paragraph>
    <Paragraph>
      A metodologia traz o diagnóstico de primeiro e segundo nível para todos os fatores relacionados ao trabalho,
      iniciando pelo diagnóstico da organização do trabalho, das condições do trabalho incluindo os fatores
      psicossociais relacionados ao trabalho.
    </Paragraph>
    <Paragraph>
      Segue tela do software Ergocheck-Premapa que avalia o valor e significado operacional da avaliação da
      exposição:
    </Paragraph>

    <View style={{ display: 'flex', justifyContent: 'center' }}>
      <Image
        src="/pdfs/bradesco/ergocheck-premapa-screen.png"
        maxWidth={480}
        ignoreFixedSize
        alt="Tela do software Ergocheck-Premapa - Valor e Significado Operacional da Avaliação da Exposição"
      />
    </View>
    <Separator weight={10} />

    <Paragraph>
      O semáforo de Severidade do método apresenta dois indicadores distintos: Exposição Intrínseca e Exposição
      Final Ponderada por Período de Exposição.
    </Paragraph>
    <Text bold size="p">
      1. Exposição Intrínseca
    </Text>
    <Bullets
      items={[
        'Refere-se ao valor bruto da exposição, calculado a partir dos fatores avaliados no diagnóstico.',
        'Esse índice não considera o fator multiplicador corretivo, ou seja, não leva em conta o tempo efetivo de exposição do trabalhador durante a jornada líquida.',
        'Serve como uma medida inicial, importante para comparar condições entre diferentes postos de trabalho ou grupos homogêneos de exposição (GHE).'
      ]}
    />
    <Text bold size="p">
      2. Exposição Final
    </Text>
    <Bullets
      items={[
        'Resulta da aplicação do fator de ponderação, que ajusta o índice considerando a realidade da jornada de trabalho (tempo líquido de exposição).',
        'Esse valor é, portanto, o que traduz a exposição efetiva do trabalhador, sendo usado como referência para enquadramento no nível de risco (inelegível, aceitável, moderado, alto ou crítico).'
      ]}
    />
    <Paragraph>Em resumo:</Paragraph>
    <Bullets
      items={[
        'Intrínseco = potencial de risco calculado sem correção pelo tempo real.',
        'Final = risco efetivo, corrigido pela jornada líquida de exposição.'
      ]}
    />
    <Paragraph>
      As gradações de severidade são 5 (cinco): Inelegível (0% a 23%); Aceitável (24% a 47,5%); Baixo (47,6% a
      73,5%); Alto (73,6% a 95%) e crítico (&gt;95%).
    </Paragraph>

    <Text semibold size="xsmall">
      Gradação da Severidade
    </Text>
    <Separator weight={4} />
    <View style={{ overflowX: 'auto' }}>
      <RedTable width="100%">
        <RedRow>
          <RedHeadCell colSpan={3}>SIGNIFICADO OPERATIVO DOS RESULTADOS</RedHeadCell>
        </RedRow>
        {GRADACAO_SEVERIDADE.map(({ classificacao, descricao, faixa }) => (
          <RedRow key={classificacao}>
            <RedDataCell
              align="center"
              bold
              bgColor={RISCO_PSICOSSOCIAL_CELL_COLORS[classificacao]}
              style={{ color: isDarkFill(classificacao) ? '#FFFFFF' : '#000000' }}
              width="14%"
            >
              <Text bold size="small" color={isDarkFill(classificacao) ? '#FFFFFF' : '#000000'}>
                {classificacao.toUpperCase()}
              </Text>
            </RedDataCell>
            <RedDataCell align="left">{descricao}</RedDataCell>
            <RedDataCell align="center" width="14%">
              {faixa}
            </RedDataCell>
          </RedRow>
        ))}
      </RedTable>
    </View>

    <Separator weight={10} />
    <Paragraph>
      Se o Resultado atingir o escore superior a 47,5% a dependência se torna elegível ao diagnóstico aprofundado
      dos fatores psicossociais, quando necessário, este diagnóstico é realizado através Comitê Técnico.
    </Paragraph>

    <SubHeading id="10.3">10.3. Matriz de Risco</SubHeading>
    <Paragraph>
      A Matriz de Risco final utilizada neste Programa de Gerenciamento de Riscos para os riscos psicossociais é
      uma matriz no formato 4x5, baseada nas estimativas de gradações de Severidade e Probabilidade do método
      ERGOCHECK.
    </Paragraph>
    <Paragraph>
      Em sintase, as gradações da probabilidade e severidade para classificação do nível de risco ocupacional, são:
    </Paragraph>

    <View style={{ display: 'flex', gap: 12 }}>
      <View style={{ flex: 1, overflowX: 'auto' }}>
        <RedTable width="100%">
          <RedRow>
            <RedHeadCell bgColor="#D9D9D9" size="small">
              PROBABILIDADE (Eventos Sentinelas)
            </RedHeadCell>
          </RedRow>
          {LEGENDA_PROBABILIDADE.map(classificacao => (
            <RedRow key={classificacao}>
              <RedDataCell
                align="center"
                bold
                bgColor={RISCO_PSICOSSOCIAL_CELL_COLORS[classificacao]}
                style={{ color: isDarkFill(classificacao) ? '#FFFFFF' : '#000000' }}
              >
                <Text bold size="small" color={isDarkFill(classificacao) ? '#FFFFFF' : '#000000'}>
                  {classificacao}
                </Text>
              </RedDataCell>
            </RedRow>
          ))}
        </RedTable>
      </View>
      <View style={{ flex: 1, overflowX: 'auto' }}>
        <RedTable width="100%">
          <RedRow>
            <RedHeadCell bgColor="#D9D9D9" size="small">
              SEVERIDADE (Ergocheck)
            </RedHeadCell>
            <RedHeadCell bgColor="#D9D9D9" size="small">
              Score
            </RedHeadCell>
          </RedRow>
          {LEGENDA_SEVERIDADE.map(({ classificacao, score }) => (
            <RedRow key={classificacao}>
              <RedDataCell
                align="center"
                bold
                bgColor={RISCO_PSICOSSOCIAL_CELL_COLORS[classificacao]}
                style={{ color: isDarkFill(classificacao) ? '#FFFFFF' : '#000000' }}
              >
                <Text bold size="small" color={isDarkFill(classificacao) ? '#FFFFFF' : '#000000'}>
                  {classificacao}
                </Text>
              </RedDataCell>
              <RedDataCell align="center" bold>
                {score}
              </RedDataCell>
            </RedRow>
          ))}
        </RedTable>
      </View>
    </View>

    <Separator weight={10} />
    <Paragraph>
      Os níveis de risco presentes na matriz são 5 (Cinco): Inelegível; Aceitável; Moderado; Alto e Crítico. Cada
      nível de risco possui o seu método de controle sugerido, baseado na estimativa (grau de certeza) da avaliação,
      onde os riscos de níveis mais altos têm prioridade de ação.
    </Paragraph>
    <Paragraph>
      A avaliação e análise dos Fatores Ergonômicos Psicossociais é realizada de maneira amostral pelos critérios
      supracitados anteriormente neste documento.
    </Paragraph>
    <Paragraph>
      Abaixo segue a Matriz de Risco com a relação final entre exposição fatores de conteúdo e contexto e eventos
      Sentinela (Dados).
    </Paragraph>

    <Text semibold size="xsmall">
      Matriz de Risco Final - Classificação de Nível de Risco Ocupacional
    </Text>
    <Separator weight={4} />
    <View style={{ overflowX: 'auto' }}>
      <RedTable width="100%" fixedLayout>
        <RedRow>
          <RedHeadCell colSpan={2} rowSpan={2} bgColor="#FFFFFF" />
          <RedHeadCell colSpan={5} bgColor={TABLE_ACCENT_DARK_RED}>
            Severidade
          </RedHeadCell>
        </RedRow>
        <RedRow>
          {MATRIZ_4X5_COLUNAS.map(({ label, numero }) => (
            <RedHeadCell key={numero} bgColor="#FFFFFF" size="xsmall">
              {label}
              <br />
              {numero}
            </RedHeadCell>
          ))}
        </RedRow>

        {MATRIZ_4X5_LINHAS.map((linha, index) => (
          <RedRow key={linha.numero}>
            {index === 0 && (
              <RedHeadCell rowSpan={MATRIZ_4X5_LINHAS.length} width="24px" bgColor={TABLE_ACCENT_DARK_RED}>
                Probabilidade
              </RedHeadCell>
            )}
            <RedHeadCell bgColor="#FFFFFF">
              {linha.label}
              <br />
              {linha.numero}
            </RedHeadCell>
            {linha.celulas.map((classificacao, celulaIndex) => (
              <RedDataCell
                key={celulaIndex}
                align="center"
                bold
                bgColor={RISCO_PSICOSSOCIAL_CELL_COLORS[classificacao]}
                style={{ color: isDarkFill(classificacao) ? '#FFFFFF' : '#000000' }}
              >
                <Text bold size="small" color={isDarkFill(classificacao) ? '#FFFFFF' : '#000000'}>
                  {classificacao}
                </Text>
              </RedDataCell>
            ))}
          </RedRow>
        ))}
      </RedTable>
    </View>

    <Separator weight={15} />

    <SubHeading id="10.4">10.4. Avaliação e Prioridade de Controle</SubHeading>
    <Paragraph>
      O Banco Bradesco S.A. estabelecerá suas prioridades e metas de avaliação e controle com base no estudo dos
      riscos ocupacionais. Essas prioridades serão definidas considerando-se os riscos psicossociais identificados,
      os custos de implantação das medidas de controle e a complexidade das atividades.
    </Paragraph>
    <Paragraph>
      A matriz de risco para os fatores psicossociais deverá ser analisada conforme o escore obtido na síntese de
      verificação, servindo como referência para a identificação e hierarquização das prioridades em todas as
      condições de trabalho relacionadas ao GHE em análise.
    </Paragraph>
    <Paragraph>
      A tabela apresentada mostra o "Valor e Significado Operacional da Avaliação da Exposição", classificando os
      resultados obtidos em cinco faixas de exposição, associadas a cores de referência e recomendações de ação:
    </Paragraph>

    <Text semibold size="xsmall">
      Avaliação e Prioridade de Controle
    </Text>
    <Separator weight={4} />
    <View style={{ overflowX: 'auto' }}>
      <RedTable width="100%">
        <RedRow>
          <RedHeadCell width="16%">Exposição</RedHeadCell>
          <RedHeadCell align="left">Resultado / Ações</RedHeadCell>
        </RedRow>
        {AVALIACAO_PRIORIDADE_CONTROLE.map(({ exposicao, resultado }) => (
          <RedRow key={exposicao}>
            <RedDataCell
              align="center"
              bold
              bgColor={RISCO_PSICOSSOCIAL_CELL_COLORS[exposicao]}
              style={{ color: isDarkFill(exposicao) ? '#FFFFFF' : '#000000' }}
            >
              <Text bold size="small" color={isDarkFill(exposicao) ? '#FFFFFF' : '#000000'}>
                {exposicao.toUpperCase()}
              </Text>
            </RedDataCell>
            <RedDataCell align="left">{resultado}</RedDataCell>
          </RedRow>
        ))}
      </RedTable>
    </View>

    <Separator weight={15} />

    <SubHeading id="10.5">10.5. Implementação das Medidas de Controle</SubHeading>
    <Paragraph>
      No âmbito da aplicação do PREMAPA, o pré-mapeamento de estresse é realizado a partir da avaliação e análise
      dos Fatores Ergonômicos Psicossociais, permitindo identificar situações críticas e orientar a definição de
      medidas preventivas e corretivas que serão tratadas em Comitê Técnico.
    </Paragraph>

    <Separator weight={15} />
  </View>
)

export default ClassificacaoRiscosErgonomicoPsicossocialSection
